import { useQueryClient, useMutation } from '@tanstack/react-query'
import { message } from 'antd'
import { useCallback, useState } from 'react'
import { triggerRun } from '../api/enrollment-overlap.api'
import type { TriggerRunResponse } from '../api/enrollment-overlap.types'
import { isRunActive } from '../utils/run-display'
import {
  ENROLLMENT_OVERLAP_QUERY_KEY,
  useLastSuccessfulRunQuery,
  useRunsQuery,
} from './useEnrollmentOverlapQueries'
import { CLOCK_TICK_MS, useNow } from './useNow'
import { usePagingParams } from './usePagingParams'
import { useRunPolling } from './useRunPolling'

export const ALREADY_RUNNING_MESSAGE = 'An audit is already running'
export const TRIGGER_FAILED_MESSAGE =
  'The audit could not be started. Please try again.'

export interface TriggerOutcomeHandlers {
  /** Another run is already in progress; `runId` is that run. */
  onAlreadyInProgress: (runId: string) => void
  /** A new run was queued. */
  onStarted: (runId: string) => void
}

/**
 * `alreadyInProgress: true` is not an error: the returned `runId` is the run
 * that is already executing, so follow that one instead of starting another.
 */
export function applyTriggerResult(
  result: TriggerRunResponse,
  handlers: TriggerOutcomeHandlers
) {
  if (result.alreadyInProgress) {
    handlers.onAlreadyInProgress(result.runId)
    return
  }
  handlers.onStarted(result.runId)
}

export function useRunsList() {
  const queryClient = useQueryClient()
  const { page, pageSize, status, setPaging, setStatus, resetToFirstPage } =
    usePagingParams()

  const runsQuery = useRunsQuery({ page, pageSize, status })
  const lastSuccessfulQuery = useLastSuccessfulRunQuery()

  // The run being followed: the one just triggered, else a genuinely active
  // RUNNING run that is visible in the list.
  const [trackedRunId, setTrackedRunId] = useState<string | null>(null)
  const [highlightedRunId, setHighlightedRunId] = useState<string | null>(null)

  // "Stalled" depends on the clock, not on the data: keep re-evaluating while
  // a run could still be active, so a run that goes past 20 minutes stops
  // blocking "Run audit now" (the next audit replaces it).
  const hasRunningRow =
    runsQuery.data?.items.some((run) => run.status === 'RUNNING') ?? false
  const now = useNow(
    hasRunningRow || trackedRunId !== null ? CLOCK_TICK_MS : null
  )

  const visibleActiveRunId =
    runsQuery.data?.items.find((run) => isRunActive(run, now))?.runId ?? null
  const pollRunId = trackedRunId ?? visibleActiveRunId

  const refreshRuns = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: [ENROLLMENT_OVERLAP_QUERY_KEY, 'runs'],
    })
  }, [queryClient])

  useRunPolling(
    pollRunId,
    useCallback(() => {
      setTrackedRunId(null)
      void refreshRuns()
    }, [refreshRuns]),
    now
  )

  const triggerMutation = useMutation({
    mutationFn: triggerRun,
    onSuccess: (result) =>
      applyTriggerResult(result, {
        onAlreadyInProgress: (runId) => {
          message.info(ALREADY_RUNNING_MESSAGE)
          setTrackedRunId(runId)
          setHighlightedRunId(runId)
          resetToFirstPage()
          void refreshRuns()
        },
        onStarted: (runId) => {
          setTrackedRunId(runId)
          setHighlightedRunId(runId)
          resetToFirstPage()
          void refreshRuns()
        },
      }),
    onError: () => {
      message.error(TRIGGER_FAILED_MESSAGE)
    },
  })

  const isAuditRunning = pollRunId !== null

  return {
    page,
    pageSize,
    status,
    setPaging,
    setStatus,
    runs: runsQuery.data,
    isLoading: runsQuery.isLoading,
    isFetching: runsQuery.isFetching,
    error: runsQuery.error,
    refetch: runsQuery.refetch,
    lastSuccessfulRun: lastSuccessfulQuery.data ?? null,
    isLastSuccessfulLoading: lastSuccessfulQuery.isLoading,
    highlightedRunId,
    isAuditRunning,
    isTriggering: triggerMutation.isPending,
    runAuditNow: () => triggerMutation.mutate(),
  }
}
